import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { getStoredAuth, eventsApi, venuesApi, uploadApi, type VenueResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, Calendar, Clock, MapPin, Ticket, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatNaira } from "@/data/venues";
import { toast } from "sonner";

export const Route = createFileRoute("/events/create")({
  head: () => ({
    meta: [
      { title: "Create Event — OyoBooking" },
      { name: "description", content: "Create a new event with tiered ticketing on OyoBooking." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CreateEventPage,
  beforeLoad: () => {
    if (typeof window === "undefined") return;
    if (!getStoredAuth()) throw redirect({ to: "/login", search: { redirect: "/events/create" } });
  },
});

type TierType = "Regular" | "VIP" | "VVIP" | "Table" | "EarlyBird";

interface TableItem {
  id: string;
  number: string;
  price: string;
}

interface TierInput {
  id: string;
  name: string;
  tierType: TierType;
  price: string;
  capacity: string;
  tables: TableItem[];
}

const TIER_TYPES: { value: TierType; label: string; emoji: string; desc: string }[] = [
  { value: "Regular", label: "Regular", emoji: "🎟️", desc: "Standard admission" },
  { value: "EarlyBird", label: "Early Bird", emoji: "⏰", desc: "Discounted for early buyers" },
  { value: "VIP", label: "VIP", emoji: "⭐", desc: "Premium access & perks" },
  { value: "VVIP", label: "VVIP", emoji: "👑", desc: "Exclusive top-tier access" },
  { value: "Table", label: "Table", emoji: "🍾", desc: "Reserved table seating" },
];

function CreateEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [venues, setVenues] = useState<VenueResponse[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [form, setForm] = useState({
    name: "",
    organizer: "",
    description: "",
    date: "",
    time: "",
    venueId: "",
    venueName: "",
    image: "",
  });
  const [tiers, setTiers] = useState<TierInput[]>([
    { id: "tier-1", name: "General Admission", tierType: "Regular", price: "", capacity: "", tables: [] },
  ]);

  const addTier = () => {
    setTiers(prev => [
      ...prev,
      { id: `tier-${Date.now()}`, name: "", tierType: "Regular", price: "", capacity: "", tables: [] },
    ]);
  };

  const addTableItem = (tierId: string) => {
    setTiers(prev => prev.map(t => t.id === tierId ? {
      ...t,
      tables: [...t.tables, { id: `tbl-${Date.now()}`, number: "", price: "" }]
    } : t));
  };

  const removeTableItem = (tierId: string, tableId: string) => {
    setTiers(prev => prev.map(t => t.id === tierId ? {
      ...t,
      tables: t.tables.filter(tbl => tbl.id !== tableId)
    } : t));
  };

  const updateTableItem = (tierId: string, tableId: string, field: keyof TableItem, value: string) => {
    setTiers(prev => prev.map(t => t.id === tierId ? {
      ...t,
      tables: t.tables.map(tbl => tbl.id === tableId ? { ...tbl, [field]: value } : tbl)
    } : t));
  };

  const removeTier = (id: string) => {
    if (tiers.length === 1) return toast.error("At least one ticket tier is required");
    setTiers(prev => prev.filter(t => t.id !== id));
  };

  const updateTier = (id: string, field: keyof TierInput, value: string) => {
    setTiers(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  useEffect(() => {
    if (!user?.id) { setVenuesLoading(false); return; }
    venuesApi.myVenues(user.id)
      .then(res => setVenues(res.data))
      .catch(() => setVenues([]))
      .finally(() => setVenuesLoading(false));
  }, [user?.id]);

  const mapTierType = (type: TierType): string => {
    const m: Record<TierType, string> = {
      Regular: "regular",
      EarlyBird: "early_bird",
      VIP: "vip",
      VVIP: "vvip",
      Table: "table",
    };
    return m[type];
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error("Event name is required");
    if (!form.organizer.trim()) return toast.error("Organizer name is required");
    if (!form.date) return toast.error("Event date is required");
    if (!form.venueName.trim()) return toast.error("Venue name is required");

    const invalidStandardTier = tiers.find(t => t.tierType !== "Table" && (!t.name.trim() || !t.price || !t.capacity));
    if (invalidStandardTier) return toast.error("Please fill in all ticket tier details");

    const invalidTableTier = tiers.find(t =>
      t.tierType === "Table" && (!t.name.trim() || t.tables.length === 0 || t.tables.some(tbl => !tbl.number.trim() || !tbl.price))
    );
    if (invalidTableTier) return toast.error("Please fill in all table numbers and prices");

    setSubmitting(true);
    try {
      const eventDate = new Date(form.date);
      const [hours, minutes] = form.time ? form.time.split(":").map(Number) : [0, 0];
      eventDate.setHours(hours, minutes);

      // Flatten Table tiers into individual tier entries per table
      const payloadTiers = tiers.flatMap(t => {
        if (t.tierType === "Table") {
          return t.tables.map(tbl => ({
            name: `${t.name.trim()} — Table ${tbl.number.trim()}`,
            tierType: mapTierType(t.tierType),
            price: Number(tbl.price),
            capacity: 1,
          }));
        }
        return [{
          name: t.name.trim(),
          tierType: mapTierType(t.tierType),
          price: Number(t.price),
          capacity: Number(t.capacity),
        }];
      });

      await eventsApi.create({
        name: form.name.trim(),
        organizer: form.organizer.trim(),
        description: form.description.trim() || undefined,
        date: eventDate.toISOString(),
        time: form.time || undefined,
        venueId: form.venueId || undefined,
        venueName: form.venueName.trim(),
        image: form.image || undefined,
        tiers: payloadTiers,
      });
      toast.success("Event created successfully!");
      navigate({ to: "/admin/dashboard" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create event";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Top bar */}
      <div className="sticky top-0 z-30 border-b border-border/50 bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <Link to="/events" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="size-4" /> Events
          </Link>
        </div>
      </div>

      <section className="mx-auto max-w-2xl px-4 pt-8 pb-20 space-y-8">
        <div>
          <h1 className="font-display text-3xl font-bold">Create Event</h1>
          <p className="mt-1 text-muted-foreground text-sm">Fill in your event details and set ticket tiers with pricing.</p>
        </div>

        {/* Event details */}
        <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
          <h3 className="font-display text-base font-semibold flex items-center gap-2">
            <Calendar className="size-4 text-primary" /> Event Details
          </h3>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Event Name <span className="text-primary">*</span></Label>
            <Input placeholder="e.g. Ibadan Music Festival 2026" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Organizer / Host Name <span className="text-primary">*</span></Label>
            <Input placeholder="e.g. Oyo Arts Council" value={form.organizer}
              onChange={e => setForm(f => ({ ...f, organizer: e.target.value }))} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description</Label>
            <Textarea rows={3} placeholder="What's this event about? Who should attend?" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Calendar className="size-3" /> Date <span className="text-primary">*</span>
              </Label>
              <Input type="date" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Clock className="size-3" /> Time
              </Label>
              <Input type="time" value={form.time}
                onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <MapPin className="size-3" /> Venue <span className="text-primary">*</span>
            </Label>
            {venuesLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                <Loader2 className="size-3 animate-spin" /> Loading venues…
              </div>
            ) : venues.length > 0 ? (
              <select
                className="h-10 w-full rounded-lg border border-border/80 bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                title="Select venue"
                value={form.venueId}
                onChange={e => {
                  const v = venues.find(ven => ven.id === e.target.value);
                  setForm(f => ({ ...f, venueId: e.target.value, venueName: v?.name ?? "" }));
                }}
              >
                <option value="">Select a venue…</option>
                {venues.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            ) : (
              <Input placeholder="e.g. Alaafin Pavilion, Ibadan" value={form.venueName}
                onChange={e => setForm(f => ({ ...f, venueName: e.target.value }))} />
            )}
          </div>

          {/* Event Image Upload */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Event Thumbnail</Label>
            {form.image ? (
              <div className="relative rounded-xl overflow-hidden border border-border/40 bg-accent/30 aspect-video">
                <img src={form.image} alt="Event thumbnail" className="size-full object-cover" />
                <button type="button" onClick={() => setForm(f => ({ ...f, image: "" }))}
                  title="Delete event thumbnail"
                  className="absolute top-2 right-2 p-1 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors">
                  <Trash2 className="size-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 bg-accent/20 p-6 cursor-pointer hover:bg-accent/30 transition-colors">
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const result = await uploadApi.image(file);
                      setForm(f => ({ ...f, image: result.url }));
                      toast.success("Image uploaded");
                    } catch {
                      toast.error("Image upload failed");
                    }
                  }}
                />
                <Plus className="size-5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Click to upload event thumbnail</span>
              </label>
            )}
          </div>
        </div>

        {/* Ticket tiers */}
        <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-semibold flex items-center gap-2">
              <Ticket className="size-4 text-primary" /> Ticket Tiers
            </h3>
            <button type="button" onClick={addTier}
              className="flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors">
              <Plus className="size-3" /> Add tier
            </button>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">Create different ticket types and set their prices. You must have at least one tier.</p>

          <div className="space-y-4">
            {tiers.map((tier, idx) => (
              <div key={tier.id} className="rounded-xl border border-border/50 bg-accent/10 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-muted-foreground">Tier {idx + 1}</span>
                  {tiers.length > 1 && (
                    <button type="button" onClick={() => removeTier(tier.id)}
                      title="Remove ticket tier"
                      className="p-1 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </div>

                {/* Tier type selector */}
                <div className="flex flex-wrap gap-2">
                  {TIER_TYPES.map(tt => (
                    <button key={tt.value} type="button"
                      onClick={() => updateTier(tier.id, "tierType", tt.value)}
                      className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all ${
                        tier.tierType === tt.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border/50 text-muted-foreground hover:border-primary/30"
                      }`}>
                      {tt.emoji} {tt.label}
                    </button>
                  ))}
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5 sm:col-span-1">
                    <Label className="text-xs font-medium">Tier Name</Label>
                    <Input placeholder="e.g. VIP Access" value={tier.name}
                      onChange={e => updateTier(tier.id, "name", e.target.value)} />
                  </div>
                  {tier.tierType === "Table" ? (
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label className="text-xs font-medium">Tables</Label>
                      <div className="space-y-2">
                        {tier.tables.map((tbl, tIdx) => (
                          <div key={tbl.id} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-5">{tIdx + 1}.</span>
                            <Input placeholder="Table #" className="h-8 text-xs w-24" value={tbl.number}
                              onChange={e => updateTableItem(tier.id, tbl.id, "number", e.target.value)} />
                            <Input type="number" min={0} step={500} placeholder="Price" className="h-8 text-xs flex-1" value={tbl.price}
                              onChange={e => updateTableItem(tier.id, tbl.id, "price", e.target.value)} />
                            {tier.tables.length > 1 && (
                              <button type="button" onClick={() => removeTableItem(tier.id, tbl.id)}
                                title="Remove table"
                                className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                                <Trash2 className="size-3" />
                              </button>
                            )}
                          </div>
                        ))}
                        <button type="button" onClick={() => addTableItem(tier.id)}
                          className="flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-[10px] font-semibold text-primary hover:bg-primary/10 transition-colors">
                          <Plus className="size-2.5" /> Add table
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Price (₦)</Label>
                        <Input type="number" min={0} step={500} placeholder="5000" value={tier.price}
                          onChange={e => updateTier(tier.id, "price", e.target.value)} />
                        {Number(tier.price) > 0 && (
                          <p className="text-[10px] text-muted-foreground">You earn ₦{Math.round(Number(tier.price) * 0.9).toLocaleString()}</p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-medium">Capacity</Label>
                        <Input type="number" min={1} step={1} placeholder="100" value={tier.capacity}
                          onChange={e => updateTier(tier.id, "capacity", e.target.value)} />
                      </div>
                    </>
                  )}
                </div>

                {tier.tierType === "Table" ? (
                  tier.tables.length > 0 && tier.tables.some(t => t.number && Number(t.price) > 0) && (
                    <div className="rounded-lg bg-background/80 border border-border/40 px-3 py-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{tier.name || "Table Tier"}</span>
                      {" · "}{TIER_TYPES.find(t => t.value === tier.tierType)?.emoji} {tier.tierType}
                      {" · "}{tier.tables.filter(t => t.number).length} tables
                      {" · "}Max revenue: <span className="font-semibold text-primary">{formatNaira(tier.tables.reduce((s, t) => s + (Number(t.price) || 0), 0))}</span>
                    </div>
                  )
                ) : (
                  tier.name && Number(tier.price) > 0 && Number(tier.capacity) > 0 && (
                    <div className="rounded-lg bg-background/80 border border-border/40 px-3 py-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{tier.name}</span>
                      {" · "}{TIER_TYPES.find(t => t.value === tier.tierType)?.emoji} {tier.tierType}
                      {" · "}{formatNaira(Number(tier.price))} × {tier.capacity} seats
                      {" · "}Max revenue: <span className="font-semibold text-primary">{formatNaira(Number(tier.price) * Number(tier.capacity))}</span>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between gap-4">
          <Link to="/events"><Button variant="ghost">Cancel</Button></Link>
          <Button size="lg" className="rounded-full px-8" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Creating event…" : "Publish Event"}
          </Button>
        </div>
      </section>
    </>
  );
}
